import { msg } from '@lingui/core/macro';

import { betterAjvErrors } from '@apideck/better-ajv-errors';
import { Immutable } from 'immer';
import { JSONSchema6 } from 'json-schema';
import compact from 'lodash/compact';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { BadInputError } from '@watershed/errors/BadInputError';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import { i18n } from '@watershed/intl';
import { formatList } from '@watershed/intl/formatters';
import { gentlyParseUnitString } from '@watershed/lscript/unit';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';

import CanonicalMetaSchema from '../CanonicalMetaSchema';
import {
  CanonicalSchemaDefinition,
  ICanonicalSchemaDefinitionField,
} from '../canonicalSchemas/canonicalSchemaUtils';
import validateReferenceDataMetaSchema from '../generated/referenceDataMetaSchema';
import { difference, intersection as setIntersection } from '../set';
import isNonEmptyArray from '@watershed/shared-util/src/isNonEmptyArray';
import { DeepReadonly, DeepWritable } from '../utils/utilTypes';
import { END_DATE_COLUMN_NAME, START_DATE_COLUMN_NAME } from './constants';
import { STABLE_ROW_ID_COL } from './stableIds';

export interface ReferenceDataSchemaDefinition
  extends CanonicalSchemaDefinition {
  properties: Record<string, ReferenceDataSchemaDefinitionField>;
  uniqueKeys?: Array<string>;
  standardizers?: Array<string>;
  standardizerOutputOnly?: Array<string>;
}

export type ReferenceDataSchemaDefinitionField =
  DeepReadonly<IReferenceDataSchemaDefinitionField>;

// All user-facing types, in a reference data schema column
export const referenceDataUserFacingTypes = [
  'string',
  'referenceQuantity',
  'referenceField',
  'referenceDate',
  'boolean',
  'date',
  'enum',
] as const;
export type ReferenceDataUserFacingType =
  (typeof referenceDataUserFacingTypes)[number];

// Only the "reference column" user-facing types.
export const userFacingTypesForReferenceColumns = [
  'referenceField',
  'referenceQuantity',
  'referenceDate',
] as const satisfies Array<ReferenceDataUserFacingType>;
export type UserFacingTypeForReferenceColumn =
  (typeof userFacingTypesForReferenceColumns)[number];

export function isUserFacingTypeForReferenceColumn(
  type: string
): type is UserFacingTypeForReferenceColumn {
  // Widen typing to allow .includes(). Sorry, couldn't find a better way.
  return (userFacingTypesForReferenceColumns as Array<string>).includes(type);
}

/**
 * Returns true if the field should be non-nullable in the DB schema.
 * Non-nullable columns:
 * - start_date, end_date
 * - referenceQuantity columns
 * - referenceField columns
 * - referenceDate columns
 * - Citation columns of referenceQuantity/referenceField/referenceDate
 *
 * The reason we have this separate logic is because we want to decouple the
 * validation from OneSchema vs ReferenceDataSchema. We generally want the
 * lookup columns to be nullable so that it plays nicely with LScript.
 */
export function isAlwaysRequiredInDb(
  fieldName: string,
  field: ReferenceDataSchemaDefinitionField,
  schema: ReferenceDataSchemaDefinition
): boolean {
  // Date range columns
  if (
    fieldName === START_DATE_COLUMN_NAME ||
    fieldName === END_DATE_COLUMN_NAME
  ) {
    return true;
  }

  // Reference columns (those with citation columns) are always required
  if (field.context?.citationColumn) {
    return true;
  }

  // Citation columns of reference columns are also always required
  for (const [_colName, colField] of Object.entries(schema.properties)) {
    if (colField.context?.citationColumn === fieldName) {
      return true;
    }
  }

  return false;
}

export interface IReferenceDataSchemaDefinitionField
  extends ICanonicalSchemaDefinitionField {
  context?: IReferenceDataSchemaFieldContext;

  /**
   * Controls whether this column is validated as required in OneSchema.
   * - If true: OneSchema validates this column as required
   * - If false: OneSchema allows this column to be optional
   * - If undefined: Falls back to checking the 'required' array (backward compatibility)
   *
   * Note: This is independent of the 'required' array, which controls DB nullability.
   * After migration, 'required' array will only contain lookup columns.
   */
  validationRequired?: boolean;
}

export type ReferenceDataSchemaFieldContext =
  DeepReadonly<IReferenceDataSchemaFieldContext>;

export interface IReferenceDataSchemaFieldContext {
  // todo: move `unit?: string` and `unitColumn?: string` here,
  // from ICanonicalSchemaDefinitionField

  externalDescription?: string;
  externalDescriptionColumn?: string;

  citation?: string;
  citationColumn?: string;

  /** @deprecated use singular `citation` */
  citations?: Array<string>;
}

export type UniqueKeyTypeList = Array<{
  name: string;
  ty: 'string' | 'number' | 'boolean' | 'integer';
}>;

/**
 * Returns an array of a schema's unique keys' names and types.
 */
export function uniqueKeyTypes(
  schema: Immutable<ReferenceDataSchemaDefinition>
): UniqueKeyTypeList {
  return (schema.uniqueKeys ?? []).map((name) => {
    const def = schema.properties[name];
    return { name, ty: def.type };
  });
}

/**
 * Returns uniqueKey columns that are required
 */
export function getRequiredUniqueKeys(
  schema: Immutable<ReferenceDataSchemaDefinition>
): Array<string> {
  return intersection(schema.uniqueKeys ?? [], schema.required ?? []);
}

export function getUnitColumns(
  schema: Immutable<ReferenceDataSchemaDefinition>
): Array<string> {
  return Object.values(schema.properties).reduce(
    (cols: Array<string>, prop) => {
      if (prop.unitColumn) cols.push(prop.unitColumn);
      return cols;
    },
    []
  );
}

export function getContextColumns(
  schema: Immutable<ReferenceDataSchemaDefinition>
): Array<string> {
  return compact(
    Object.values(schema.properties).flatMap((prop) => [
      prop.context?.citationColumn,
      prop.context?.externalDescriptionColumn,
    ])
  );
}

export function getContextForColumn(
  schema: ReferenceDataSchemaDefinition,
  column: string
): ReferenceDataSchemaFieldContext {
  const property = schema.properties[column];
  UnexpectedError.invariant(
    property,
    `Unable to find column in schema: ${column}`,
    { data: { column, schema } }
  );
  return property.context ?? {};
}

export const validateReferenceDataSchemaUnits = (
  schema: ReferenceDataSchemaDefinition
): Array<string> => {
  const validationMsgs = new Array<string>();
  const unitCols = getUnitColumns(schema);

  for (const [col, colEntry] of Object.entries(schema.properties)) {
    // Reference date columns (dates with citation columns) should not have units
    if (
      colEntry.format === 'date' &&
      colEntry.context?.citationColumn &&
      (colEntry.unit || colEntry.unitColumn)
    ) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `Reference date column "${col}" should not have units.`,
            context: 'Validation error when a reference date column has units.',
          })
        )
      );
    }

    if (colEntry.unit && !gentlyParseUnitString(colEntry.unit)) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `Invalid unit "${colEntry.unit}" specified for column "${col}".`,
            context:
              'Validation error when a column specifies an invalid unit.',
          })
        )
      );
    }

    if (colEntry.unitColumn) {
      const unitCol = schema.properties[colEntry.unitColumn];
      if (!unitCol) {
        validationMsgs.push(
          i18n._(
            msg({
              message: `Unknown column "${colEntry.unitColumn}" referenced as unit column of "${col}".`,
              context:
                "Validation error when referencing a unit column that doesn't exist.",
            })
          )
        );
      }
    }

    if (unitCols.includes(col)) {
      if (colEntry.type !== 'string') {
        validationMsgs.push(
          i18n._(
            msg({
              message: `Unit column "${col}" should have type string.`,
              context:
                'Validation error when a unit column has wrong data type.',
            })
          )
        );
      } else if (!colEntry.enum || colEntry.enum.length === 0) {
        validationMsgs.push(
          i18n._(
            msg({
              message: `Unit column "${col}" should have unit enums specified.`,
              context:
                'Validation error when a unit column lacks required enum values.',
            })
          )
        );
      } else {
        for (const unitName of colEntry.enum) {
          // TODO (CALC-2561): `enum` is typed as Array<string> but some
          // schemas have enums containing null values, so check for that here
          if (unitName && !gentlyParseUnitString(unitName)) {
            validationMsgs.push(
              i18n._(
                msg({
                  message: `"${unitName}" from unit column "${col}" is not a valid unit.`,
                  context:
                    'Validation error when a unit column contains an invalid unit as an enum option.',
                })
              )
            );
          }
        }
      }
    }
  }

  return validationMsgs;
};

export const validateReferenceDataSchemaDates = (
  schema: ReferenceDataSchemaDefinition
): Array<string> => {
  const validationMsgs = new Array<string>();
  const dateColumns = compact(
    Object.entries(schema.properties).filter(
      ([col, _]) =>
        col === START_DATE_COLUMN_NAME || col === END_DATE_COLUMN_NAME
    )
  );

  for (const [col, colEntry] of dateColumns) {
    // CALC-2975 this should actually be date type!
    if (colEntry.type !== 'string') {
      validationMsgs.push(
        i18n._(
          msg({
            message: `Column "${col}" should have type "string".`,
            context: 'Validation error when date column has wrong data type.',
          })
        )
      );
    }

    if (!colEntry.format || colEntry.format !== 'date') {
      validationMsgs.push(
        i18n._(
          msg({
            message: `Column "${col}" should have format "date".`,
            context: 'Validation error when date column has wrong format.',
          })
        )
      );
    }
  }

  return validationMsgs;
};

export function getCitationColumns(
  schema: ReferenceDataSchemaDefinition
): Array<string> {
  return Object.values(schema.properties)
    .map((prop) => prop.context?.citationColumn)
    .filter(isNotNullish);
}

export function getCitationSlugs(
  schema: ReferenceDataSchemaDefinition
): Array<string> {
  return Object.values(schema.properties)
    .map((colEntry) => colEntry.context?.citation)
    .filter(isNotNullish);
}

export function getExternalDescriptionColumns(
  schema: ReferenceDataSchemaDefinition
): Array<string> {
  return Object.values(schema.properties)
    .map((prop) => prop.context?.externalDescriptionColumn)
    .filter(isNotNullish);
}

type SchemaContextColumns = {
  schema: ReferenceDataSchemaDefinition;
  citationCols: Array<string>;
  externalDescriptionCols: Array<string>;
  allCols: Array<string>;
};

/**
 * Checks that context columns all exist & one column isn't multiple forms of context
 */
function validateReferenceDataSchemaContextColumnsExist(
  schemaCtx: SchemaContextColumns
): Array<string> {
  const validationMsgs: Array<string> = [];
  const citationDescriptionIntersect = intersection(
    schemaCtx.citationCols,
    schemaCtx.externalDescriptionCols
  ).filter((c) => c !== ''); // filter out empty strings b/c caught in validate as strings
  if (citationDescriptionIntersect.length > 0) {
    validationMsgs.push(
      i18n._(
        msg({
          message: `Columns cannot be both citation and description columns: ${formatList(
            citationDescriptionIntersect,
            { locale: i18n.locale }
          )}`,
          context:
            'Validation error when columns are both citation and description.',
        })
      )
    );
  }

  schemaCtx.citationCols.forEach((citationCol) => {
    if (!schemaCtx.allCols.includes(citationCol) && citationCol !== '')
      validationMsgs.push(
        i18n._(
          msg({
            message: `Citation column "${citationCol}" does not exist.`,
            context: 'Validation error when citation column does not exist.',
          })
        )
      );
  });
  schemaCtx.externalDescriptionCols.forEach((descriptionCol) => {
    if (!schemaCtx.allCols.includes(descriptionCol) && descriptionCol !== '')
      validationMsgs.push(
        i18n._(
          msg({
            message: `Description column "${descriptionCol}" does not exist.`,
            context: 'Validation error when description column does not exist.',
          })
        )
      );
  });

  return validationMsgs;
}

/**
 * Checks that context columns have type string & column names aren't empty strings
 */
function validateReferenceDataSchemaContextColumnsStrings(
  schemaCtx: SchemaContextColumns
): Array<string> {
  const validationMsgs: Array<string> = [];

  for (const [col, colEntry] of Object.entries(schemaCtx.schema.properties)) {
    if (schemaCtx.citationCols.includes(col) && colEntry.type !== 'string') {
      validationMsgs.push(
        i18n._(
          msg({
            message: `Citation column "${col}" should be of type string.`,
            context:
              'Validation error when citation column has wrong data type.',
          })
        )
      );
    }

    if (
      schemaCtx.externalDescriptionCols.includes(col) &&
      colEntry.type !== 'string'
    ) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `External description column "${col}" should be of type string.`,
            context:
              'Validation error when description column has wrong data type.',
          })
        )
      );
    }

    if (
      colEntry.context &&
      'citation' in colEntry.context &&
      !colEntry.context.citation
    ) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `"${col}" should not have blank citation.`,
            context: 'Validation error when citation field is empty.',
          })
        )
      );
    }

    if (
      colEntry.context &&
      'citationColumn' in colEntry.context &&
      !colEntry.context.citationColumn
    ) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `"${col}" should not have blank citation column.`,
            context:
              'Validation error when citation column reference is empty.',
          })
        )
      );
    }

    if (
      colEntry.context &&
      'externalDescription' in colEntry.context &&
      !colEntry.context.externalDescription
    ) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `"${col}" should not have blank external description.`,
            context: 'Validation error when description field is empty.',
          })
        )
      );
    }

    if (
      colEntry.context &&
      'externalDescriptionColumn' in colEntry.context &&
      !colEntry.context.externalDescriptionColumn
    ) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `"${col}" should not have blank external description column.`,
            context:
              'Validation error when description column reference is empty.',
          })
        )
      );
    }
  }

  return validationMsgs;
}

/**
 * Checks that context columns don't have context
 */
function validateReferenceDataSchemaContextColumnsNotNested(
  schemaCtx: SchemaContextColumns
): Array<string> {
  const validationMsgs: Array<string> = [];

  for (const [col, colEntry] of Object.entries(schemaCtx.schema.properties)) {
    if (schemaCtx.citationCols.includes(col) && colEntry.context) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `Citation column "${col}" should not have context.`,
            context:
              'Validation error when citation column itself has context (nested context not allowed).',
          })
        )
      );
    }

    if (schemaCtx.externalDescriptionCols.includes(col) && colEntry.context) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `External description column "${col}" should not have context.`,
            context:
              'Validation error when description column itself has context (nested context not allowed).',
          })
        )
      );
    }
  }

  return validationMsgs;
}

export function validateReferenceDataSchemaContext(
  schema: ReferenceDataSchemaDefinition
): Array<string> {
  const schemaContext: SchemaContextColumns = {
    schema,
    citationCols: getCitationColumns(schema),
    externalDescriptionCols: getExternalDescriptionColumns(schema),
    allCols: Object.keys(schema.properties),
  };

  return validateReferenceDataSchemaContextColumnsExist(schemaContext)
    .concat(validateReferenceDataSchemaContextColumnsStrings(schemaContext))
    .concat(validateReferenceDataSchemaContextColumnsNotNested(schemaContext));
}

export function validateReferenceDataSchema(schemaJson: object): Array<string> {
  const validator = validateReferenceDataMetaSchema;

  if (!validator(schemaJson)) {
    const errorMessages = betterAjvErrors({
      // betterAjvErrors expects a JSONSchema6 object, but CanonialMetaSchema
      // is typed as a JSONSchema7 object. This seems to work fine because
      // we're not using any features unique to JSONSchema7.
      schema: CanonicalMetaSchema as JSONSchema6,
      data: schemaJson,
      errors: validator.errors,
      basePath: 'Schema',
    }).map((x) => x.message);
    return errorMessages;
  }

  // If the schema is shaped correctly, validate logical things
  const schema = schemaJson as ReferenceDataSchemaDefinition;
  return [
    ...validateReferenceDataSchemaUnits(schema),
    ...validateReferenceDataSchemaContext(schema),
    ...validateReferenceDataSchemaDates(schema),
    ...validateColumnsExist(schema, 'unique keys', schema.uniqueKeys ?? []),
    ...validateColumnsExist(schema, 'required columns', schema.required ?? []),
  ];
}

function validateColumnsExist(
  schema: ReferenceDataSchemaDefinition,
  propertyName: string,
  cols: ReadonlyArray<string>
): Array<string> {
  const validationMsgs: Array<string> = [];
  for (const col of cols) {
    if (!schema.properties[col]) {
      validationMsgs.push(
        i18n._(
          msg({
            message: `Unknown column "${col}" referenced in "${propertyName}".`,
            context: 'Validation error when referenced column does not exist.',
          })
        )
      );
    }
  }
  return validationMsgs;
}

export function isValidReferenceDataSchema(
  schemaObj: object
): schemaObj is ReferenceDataSchemaDefinition {
  return validateReferenceDataSchema(schemaObj).length === 0;
}

// TODO: roll this into isValidReferenceDataSchema (it's currently separate
// because we need to call it from the frontend, where we can't use AJV).
export function isEmptySchema(schema: ReferenceDataSchemaDefinition): boolean {
  const val = isEmpty(schema.properties) || isEmpty(schema.uniqueKeys);
  return val;
}

/**
 * Checks that the existing and incoming schemas differ in only ways that are
 * allowable for published schemas (i.e. only context changes and adding new
 * enum values).
 */
export function validateReferenceDataSchemaUpdates(
  existing: ReferenceDataSchemaDefinition,
  incoming: ReferenceDataSchemaDefinition,
  options: {
    allowAddingUnitContext?: boolean;
  } = {}
): Array<string> {
  const errors: Array<string> = [];
  const newColumns = new Set(Object.keys(incoming.properties));

  // Check that all the columns in the existing schema are in the incoming
  // schema, and that they don't have any unexpected changes
  for (const [colName, colDef] of Object.entries(existing.properties)) {
    if (!incoming.properties[colName]) {
      errors.push(
        i18n._(
          msg({
            message: `Missing column: "${colName}".`,
            context:
              'Validation error when updating a schema illegally removes an existing column.',
          })
        )
      );
      continue;
    } else {
      newColumns.delete(colName);
    }

    const incomingColDef = incoming.properties[colName];

    // Check type compatibility
    if (colDef.type !== incomingColDef.type) {
      errors.push(
        i18n._(
          msg({
            message: `Expected "${colName}" to be type "${colDef.type}", got "${incomingColDef.type}".`,
            context:
              'Validation error when updating a schema illegally changes column data type.',
          })
        )
      );
    }

    // Check units
    errors.push(
      ...checkForOptionalPropertyEquality(
        colName,
        'unit',
        colDef.unit,
        incomingColDef.unit,
        options.allowAddingUnitContext
      ),
      ...checkForOptionalPropertyEquality(
        colName,
        'unit column',
        colDef.unitColumn,
        incomingColDef.unitColumn,
        options.allowAddingUnitContext
      )
    );

    // Check enums
    if (isNonEmptyArray(colDef.enum)) {
      const missingValues = difference(
        new Set(colDef.enum),
        new Set(incomingColDef.enum)
      );
      if (missingValues.size > 0) {
        errors.push(
          i18n._(
            msg({
              message: `Missing enum values for "${colName}": ${formatList(
                [...missingValues],
                { locale: i18n.locale }
              )}.`,
              context:
                'Validation error when updating a schema removes enum values.',
            })
          )
        );
      }
    }
  }

  // Check that there's no new columns
  if (newColumns.size > 0) {
    for (const column of newColumns) {
      errors.push(
        i18n._(
          msg({
            message: `New column: "${column}".`,
            context:
              'Validation error when updating a schema illegally adds a new column.',
          })
        )
      );
    }
  }

  errors.push(
    ...checkForSetEquality(
      existing.uniqueKeys,
      incoming.uniqueKeys,
      (missingValue) =>
        i18n._(
          msg({
            message: `Missing unique key: "${missingValue}".`,
            context:
              'Validation error when updating a schema illegally removes a unique key.',
          })
        ),
      (newValue) =>
        i18n._(
          msg({
            message: `Unexpected unique key: "${newValue}".`,
            context:
              'Validation error when updating a schema illegally adds a new unique key.',
          })
        )
    )
  );

  errors.push(
    ...checkForSetEquality(
      existing.required,
      incoming.required,
      (missingValue) =>
        i18n._(
          msg({
            message: `Expected "${missingValue}" to be required.`,
            context:
              'Validation error when updating a schema illegally makes a required field optional.',
          })
        ),
      (newValue) =>
        i18n._(
          msg({
            message: `Expected "${newValue}" to be non-required.`,
            context:
              'Validation error when updating a schema illegally makes an optional field required.',
          })
        )
    )
  );

  return errors;
}

export function checkReferenceDataSchemaUpdatesForWarnings(
  existing: ReferenceDataSchemaDefinition,
  incoming: ReferenceDataSchemaDefinition
): Array<string> {
  const warnings: Array<string> = [];

  for (const [colName, colDef] of Object.entries(existing.properties)) {
    if (!incoming.properties[colName]) {
      // we handle this in errors validation
      continue;
    }
    const incomingColDef = incoming.properties[colName];
    if (
      (colDef.context?.citation || colDef.context?.citationColumn) &&
      !incomingColDef.context?.citation &&
      !incomingColDef.context?.citationColumn
    ) {
      warnings.push(
        i18n._(
          msg({
            message: `Citation was removed from column "${colName}".`,
            context: 'Warning when updating a schema removes citation.',
          })
        )
      );
    }
    if (
      (colDef.context?.externalDescription ||
        colDef.context?.externalDescriptionColumn) &&
      !incomingColDef.context?.externalDescription &&
      !incomingColDef.context?.externalDescriptionColumn
    ) {
      warnings.push(
        i18n._(
          msg({
            message: `External description was removed from column "${colName}".`,
            context: 'Warning when updating a schema removes description.',
          })
        )
      );
    }
  }
  return warnings;
}

function checkForOptionalPropertyEquality<T>(
  columnName: string,
  propertyName: string,
  existing: T | null | undefined,
  incoming: T | null | undefined,
  allowNullToNonNull: boolean = false
): Array<string> {
  const errors: Array<string> = [];

  if (!existing && incoming && !allowNullToNonNull) {
    errors.push(
      i18n._(
        msg({
          message: `Expected "${columnName}" to have no ${propertyName}, got "${incoming}".`,
          context:
            'Validation error when updating a schema adds a property (like a "unit" or a "description") where none existed. "incoming" refers to the new value (of the unit, description, etc).',
        })
      )
    );
  } else if (existing && existing !== incoming) {
    errors.push(
      i18n._(
        msg({
          message: `Expected "${columnName}" to have no ${propertyName} "${existing}", got "${incoming ?? 'undefined'}".`,
          context:
            'Validation error when updating a schema changes a property value (like a "unit" or a "description"). "incoming" refers to the new value (of the unit, description, etc) and "existing" refers to the old value.',
        })
      )
    );
  }

  return errors;
}

function checkForSetEquality(
  existing: ReadonlyArray<string> | undefined,
  incoming: ReadonlyArray<string> | undefined,
  missingValueErrorMessage: (value: string) => string,
  newValueErrorMessage: (value: string) => string
): Array<string> {
  const errors: Array<string> = [];

  const existingValues = new Set(existing ?? []);
  const incomingValues = new Set(incoming ?? []);

  const missingValues = difference(existingValues, incomingValues);
  for (const value of missingValues) {
    errors.push(missingValueErrorMessage(value));
  }

  const newValues = difference(incomingValues, existingValues);
  for (const value of newValues) {
    errors.push(newValueErrorMessage(value));
  }

  return errors;
}

export function hasDateKeyColumns(uniqueKeys: ReadonlyArray<string>): boolean {
  return (
    uniqueKeys.includes(START_DATE_COLUMN_NAME) &&
    uniqueKeys.includes(END_DATE_COLUMN_NAME)
  );
}

export function getNonDateKeyColumns(
  uniqueKeys: ReadonlyArray<string>
): Array<string> {
  return uniqueKeys.filter(
    (key) => key !== START_DATE_COLUMN_NAME && key !== END_DATE_COLUMN_NAME
  );
}

export function areSchemasEqual(
  existing: ReferenceDataSchemaDefinition,
  incoming: ReferenceDataSchemaDefinition
): boolean {
  return isEqual(normalizeSchema(existing), normalizeSchema(incoming));
}

function normalizeSchema(
  schema: ReferenceDataSchemaDefinition
): ReferenceDataSchemaDefinition {
  // Sort arrays where order doesn't matter so we can compare them reliably
  return {
    ...schema,
    required: [...(schema.required ?? [])].sort(),
    standardizers: [...(schema.standardizers ?? [])].sort(),
    standardizerOutputOnly: [...(schema.standardizerOutputOnly ?? [])].sort(),
    uniqueKeys: [...(schema.uniqueKeys ?? [])].sort(),
  };
}

export function getSchemaChanges(
  existing: ReferenceDataSchemaDefinition,
  incoming: ReferenceDataSchemaDefinition
): Array<string> {
  const schemaChanges: Array<string> = [];
  if (areSchemasEqual(existing, incoming)) {
    return schemaChanges;
  }

  const one: ReferenceDataSchemaDefinition = normalizeSchema(existing);
  const two: ReferenceDataSchemaDefinition = normalizeSchema(incoming);

  // Check schema metadata
  function diff(field: string, e: string | undefined, i: string | undefined) {
    if (!isEqual(e, i)) {
      schemaChanges.push(
        `Schema \`${field}\` changed from \`${e}\` to \`${i}\``
      );
    }
  }
  diff('title', one.title, two.title);
  diff('description', one.description, two.description);
  diff('type', one.type, two.type);
  // Using JSON.stringify() for the arrays instead of isEqual()
  // immediately so we can take advantage of diff() and avoid writing
  // another function that checks isEqual and conditionally stringifies
  diff(
    'standardizers',
    JSON.stringify(one.standardizers),
    JSON.stringify(two.standardizers)
  );
  diff(
    'standardizerOutputOnly',
    JSON.stringify(one.standardizerOutputOnly),
    JSON.stringify(two.standardizerOutputOnly)
  );
  diff('required', JSON.stringify(one.required), JSON.stringify(two.required));
  diff(
    'uniqueKeys',
    JSON.stringify(one.uniqueKeys),
    JSON.stringify(two.uniqueKeys)
  );

  // Check that both schemas have the same columns
  const props1: ReadonlySet<string> = new Set(Object.keys(one.properties));
  const props2: ReadonlySet<string> = new Set(Object.keys(two.properties));
  difference(props1, props2).forEach((lostProp) =>
    schemaChanges.push(`Lost column \`${lostProp}\``)
  );
  difference(props2, props1).forEach((gainedProp) => {
    if (gainedProp !== STABLE_ROW_ID_COL)
      schemaChanges.push(`Gained column \`${gainedProp}\``);
  });

  setIntersection(props1, props2).forEach((propName) => {
    const prop1: ReferenceDataSchemaDefinitionField = one.properties[propName];
    const prop2: ReferenceDataSchemaDefinitionField = two.properties[propName];

    function diffCol(field: string, e: any, i: any) {
      if (propName.includes('date') && ['type', 'format'].includes(field)) {
        // todo: CALC-2573
        // date diffs are a result of using old schema inference
        // manually silencing them until migrating to new schema infer
        return;
      }

      // eg. if one is undefined and the other is empty string
      const isBothEmpty = isEmpty(e) && isEmpty(i);
      if (!isEqual(e, i) && !isBothEmpty) {
        schemaChanges.push(
          `Column \`${propName}\` changed field \`${field}\` from ` +
            `\`${e}\` to \`${i}\``
        );
      }
    }
    diffCol('type', prop1.type, prop2.type);
    diffCol('description', prop1.description, prop2.description);
    diffCol('format', prop1.format, prop2.format);
    diffCol('enum', prop1.enum, prop2.enum);
    diffCol('examples', prop1.examples, prop2.examples);
    diffCol('default', prop1.default, prop2.default);
    diffCol('displayName', prop1.displayName, prop2.displayName);
    diffCol('unit', prop1.unit, prop2.unit);
    diffCol('unitColumn', prop1.unitColumn, prop2.unitColumn);
    diffCol(
      'context.externalDescription',
      prop1.context?.externalDescription,
      prop2.context?.externalDescription
    );
    diffCol(
      'context.externalDescriptionColumn',
      prop1.context?.externalDescriptionColumn,
      prop2.context?.externalDescriptionColumn
    );
    diffCol(
      'context.citation',
      prop1.context?.citation,
      prop2.context?.citation
    );
    diffCol(
      'context.citationColumn',
      prop1.context?.citationColumn,
      prop2.context?.citationColumn
    );
  });

  return schemaChanges;
}

export function removeReferenceDataSchemaProperty(
  schema: DeepWritable<CanonicalSchemaDefinition>,
  property: string
): CanonicalSchemaDefinition {
  if (property in schema.properties) {
    delete schema.properties[property];
    schema.required = schema.required?.filter((p: string) => p !== property);
  }
  return schema as CanonicalSchemaDefinition;
}

export const referenceDataSchemaSimplifiedTypes = [
  'string',
  'number',
  'integer',
  'date',
  'datetime',
  'boolean',
] as const;

type ReferenceDataSchemaSimplifiedType =
  (typeof referenceDataSchemaSimplifiedTypes)[number];

/**
 * When we want to represent a column's type with one string, we can do so with
 * `ReferenceDataSchemaSimplifiedType`. This function returns the simplified
 * type for a column in a schema.
 */
export function getSimplifiedTypeForColumn(
  schema: Immutable<Pick<ReferenceDataSchemaDefinition, 'properties'>>,
  name: string
): ReferenceDataSchemaSimplifiedType {
  const columnSchema = schema.properties[name];
  BadInputError.invariant(
    !!columnSchema,
    'Tried to get the schema for a column that does not exist',
    { data: { schema, name } }
  );
  if (columnSchema.type === 'string' && columnSchema.format === 'date') {
    return 'date';
  }
  if (columnSchema.type === 'string' && columnSchema.format === 'date-time') {
    return 'datetime';
  }
  return columnSchema.type;
}
